/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../../store';
import {
    IOption,
    IOrderDetails,
    IPairAidPart,
    IProductDetail,
    ISelectedOptionIds,
    sides,
    IAccessoryOrder,
} from '../../../models/product-detail';
import {
    ACO_CHARGER_OPTIONS_ID,
    BATTERY_PRODUCT_TYPE,
    CartItemTypes,
    CHARGER_OPTIONS_ID,
    chargerMultiOptionCountries,
    CHARGERS_OPTION_TYPE_ID,
    countriesOldAccessories,
    ME_CHARGER_OPTIONS_ID,
    ME_CHARGERS_OPTION_TYPE_ID,
    NO_CHARGER,
} from '../../common/constants';
import RowElementWrapper from '../../common/row-element-wrapper';
import MultiOption from './options-components/multi';
import OptionsRadio from './options-components/options-radio';
import OptionsSelectDropDown from './options-components/options-select-drop-down';
import SingleOptionType from './options-components/single';
import { ProductOrderFormErrors } from './validation';
import { findOption, showHideEarmolOptionsBasedOnCROS } from './rules-engine';
import '../../../utils/array-prototypes';
import { IUpdateOptionData } from '../../../models/order';
import { ISession } from '../../../models/pro-sites';
import AccessoryElementIntl from './accessory-element-intl';
import './product-options.css';
import DualSelect from './options-components/dual-select';
import SingleInline from './options-components/single-inline';
import DualSingle from './options-components/dual-single';
import OptionHeader from './options-components/option-header';
import GreenCheckMark from './options-components/green-check-mark';

interface IProductOptionsProps {
    options: IOption[];
    productDetails: IProductDetail;
    orderDetails: IOrderDetails;
    updateSelectedOptions: (
        rightSelectedIds: number[],
        leftSelectedIds: number[],
        isEarmold: boolean
    ) => void;
    isEarmold: boolean;
    optionObject?: IOption;
    updateCrosPairAid: (pairAid?: IPairAidPart) => void;
    errors: ProductOrderFormErrors;
    updateOptionData: IUpdateOptionData | null;
    session: ISession | null | undefined;
    onAddDeleteAccessory?: (
        partNumber: string,
        quantity: number,
        isCharger?: boolean,
        chargerToDelete?: string
    ) => void;
    selectedAccessories?: IAccessoryOrder[];
    patientOrder?: boolean;
    isUpdateDetails?: boolean;
}

const ProductOptions = ({
    options,
    productDetails,
    orderDetails,
    updateSelectedOptions,
    isEarmold,
    optionObject,
    updateCrosPairAid,
    errors,
    updateOptionData,
    session,
    onAddDeleteAccessory,
    selectedAccessories,
    patientOrder,
    isUpdateDetails,
}: IProductOptionsProps) => {
    const intl = useIntl();
    const [selectedIdsState, setSelectedIdsState] =
        useState<ISelectedOptionIds>({
            rightSelectedIds: isEarmold
                ? orderDetails.rightEarmoldIds
                : orderDetails.rightSelectedIds,
            leftSelectedIds: isEarmold
                ? orderDetails.leftEarmoldIds
                : orderDetails.leftSelectedIds,
        });
    const [colorSwatches, setColorSwatches] = useState(false);

    const chargerOptions = options.find(
        (option) =>
            option.id === CHARGER_OPTIONS_ID ||
            option.id === ME_CHARGER_OPTIONS_ID ||
            option.id === ACO_CHARGER_OPTIONS_ID
    );
    const defaultCharger = chargerOptions
        ? chargerOptions!.items[0].items.find((charger) => charger.isDefault)
        : undefined;
    const isChargerSelected = selectedAccessories
        ? selectedAccessories!.find((accessory) => accessory.isCharger)
        : undefined;
    const getStockOrderChargersQuantity = (): number =>
        Math.round(orderDetails.quantity / 2);
    const resetChargersMaxCount = (): void => {
        if (onAddDeleteAccessory && chargerOptions) {
            if (patientOrder || orderDetails.isCustom) {
                onAddDeleteAccessory(
                    !isChargerSelected
                        ? defaultCharger
                            ? defaultCharger!.partNumber
                            : ''
                        : isChargerSelected
                        ? isChargerSelected!.partNumber
                        : '',
                    1,
                    true
                );
            } else {
                onAddDeleteAccessory(
                    !isChargerSelected && !productDetails!.existing
                        ? defaultCharger
                            ? defaultCharger!.partNumber
                            : ''
                        : isChargerSelected
                        ? isChargerSelected!.partNumber
                        : '',
                    getStockOrderChargersQuantity(),
                    true
                );
            }
        }
    };

    const isChargerMulti = (optionTypeId: number) =>
        (optionTypeId === ME_CHARGERS_OPTION_TYPE_ID && !isUpdateDetails) ||
        (optionTypeId === CHARGERS_OPTION_TYPE_ID &&
            patientOrder &&
            chargerMultiOptionCountries.includes(session!.country) &&
            !isUpdateDetails);
    const isChargerRadio = (optionTypeId: number) =>
        optionTypeId === CHARGERS_OPTION_TYPE_ID &&
        patientOrder &&
        !chargerMultiOptionCountries.includes(session!.country) &&
        !isUpdateDetails;

    const isChargerAccessory = (optionTypeId: number) =>
        optionTypeId === CHARGERS_OPTION_TYPE_ID && !isUpdateDetails;

    const addSelectsToOrder = (
        optionsAvailable: IOption[],
        event: React.ChangeEvent<any>,
        side: string
    ) => {
        const right: number[] = JSON.parse(
            JSON.stringify(selectedIdsState.rightSelectedIds)
        );
        const left: number[] = JSON.parse(
            JSON.stringify(selectedIdsState.leftSelectedIds)
        );
        optionsAvailable.forEach((x) => {
            switch (side) {
                case 'right':
                    if (x.id === parseInt(event.target.value)) {
                        if (!right.includes(parseInt(event.target.value)))
                            right.push(parseInt(event.target.value));
                    } else {
                        if (right.indexOf(x.id) >= 0)
                            right.splice(right.indexOf(x.id), 1);
                    }
                    break;
                case 'left':
                    if (x.id === parseInt(event.target.value)) {
                        if (!left.includes(parseInt(event.target.value)))
                            left.push(parseInt(event.target.value));
                    } else {
                        if (left.indexOf(x.id) >= 0)
                            left.splice(left.indexOf(x.id), 1);
                    }
                    break;
                case 'both':
                    if (x.id === parseInt(event.target.value)) {
                        if (!right.includes(parseInt(event.target.value)))
                            right.push(parseInt(event.target.value));
                        if (!left.includes(parseInt(event.target.value)))
                            left.push(parseInt(event.target.value));
                    } else {
                        if (right.indexOf(x.id) >= 0)
                            right.splice(right.indexOf(x.id), 1);
                        if (left.indexOf(x.id) >= 0)
                            left.splice(left.indexOf(x.id), 1);
                    }
            }
        });

        updateSelectedOptions(right, left, isEarmold);
    };

    const updateOptionsSelection = (right: number[], left: number[]) => {
        if (
            productDetails.earmoldOptions &&
            productDetails.earmoldOptions.length > 0 &&
            productDetails.isCros &&
            !isEarmold
        ) {
            /// modify earmold options availability based on CROS selection
            const updatedEarmoldIds = showHideEarmolOptionsBasedOnCROS(
                {
                    ...orderDetails,
                    rightSelectedIds: right,
                    leftSelectedIds: left,
                },
                {
                    rightSelectedIds: orderDetails.rightEarmoldIds,
                    leftSelectedIds: orderDetails.leftEarmoldIds,
                }
            );

            const tempright = updatedEarmoldIds.rightSelectedIds;
            const templeft = updatedEarmoldIds.leftSelectedIds;

            updateSelectedOptions(right, left, isEarmold);
            updateSelectedOptions(tempright, templeft, true);
        } else {
            updateSelectedOptions(right, left, isEarmold);
        }
    };

    const isOptionCategoryVisible = (category: IOption) => {
        if (
            category.id === 16 &&
            orderDetails.cartItemTypeId === CartItemTypes.RicWithEarmold &&
            orderDetails.cros &&
            orderDetails.side !== 'B' &&
            orderDetails.rightSelectedIds.includes(510) &&
            orderDetails.leftSelectedIds.includes(510) &&
            orderDetails.rightEarmoldIds.includes(510) &&
            orderDetails.leftEarmoldIds.includes(510)
        )
            return false; // Hard-coded hide venting for monaural CROS RIC w/Earmold

        if (
            orderDetails.side != null &&
            category.id === 7 &&
            orderDetails.cartItemTypeId === CartItemTypes.RicWithAp &&
            orderDetails.cros &&
            orderDetails.side !== 'B' &&
            (findOption(711, productDetails.options) ||
                findOption(1307, productDetails.options)) &&
            (orderDetails.rightSelectedIds.includes(711) ||
                orderDetails.rightSelectedIds.includes(1307)) &&
            (orderDetails.leftSelectedIds.includes(711) ||
                orderDetails.leftSelectedIds.includes(1307))
        ) {
            // Hard-coded show venting only on AP side if transmitter side omits AP receiver

            return (
                (orderDetails.side === 'R' &&
                    orderDetails.rightSelectedIds.includes(511)) ||
                (orderDetails.side === 'L' &&
                    orderDetails.leftSelectedIds.includes(511))
            );
        }

        if (category.id === 47 && orderDetails.side !== 'B') return false;

        ///hide cros earmold question for monaural CROS
        if (category.id === 43 && orderDetails.side !== 'B') return false;
        if ((category.id === 8 || category.id === 19) && updateOptionData)
            return false;
        return (
            category.items.length > 0 &&
            category.items.some((optionType) => {
                return optionType.items.some((option) => {
                    if (orderDetails.side === 'R') {
                        return option.isAvailableRight && !option.hidden;
                    } else if (orderDetails.side === 'L') {
                        return option.isAvailableLeft && !option.hidden;
                    } else {
                        return (
                            (option.isAvailableLeft ||
                                option.isAvailableRight) &&
                            !option.hidden
                        );
                    }
                });
            })
        );
    };

    useEffect(() => {
        setSelectedIdsState((prevState) => ({
            ...prevState,
            rightSelectedIds: isEarmold
                ? orderDetails.rightEarmoldIds
                : orderDetails.rightSelectedIds,
            leftSelectedIds: isEarmold
                ? orderDetails.leftEarmoldIds
                : orderDetails.leftSelectedIds,
        }));
    }, [orderDetails]);

    useEffect(() => {
        resetChargersMaxCount();
    }, [orderDetails.quantity, patientOrder, orderDetails.isCustom]);

    return (
        selectedIdsState.rightSelectedIds &&
        selectedIdsState.leftSelectedIds &&
        options && (
            <div style={{ marginBottom: '25px' }}>
                <RowElementWrapper>
                    {options.map((option, i) => {
                        if (
                            //#region OptionCategories
                            // 7-StarkeyChoice
                            // 12-Hidden options for PDF
                            // 13-Order Options
                            // 14-Receiver
                            // 8-Receiver Cable Length
                            // 19-Receiver Gain
                            //#endregion
                            ///Earmold Options
                            ([
                                CartItemTypes.BteWithEarmold,
                                CartItemTypes.RicWithEarmold,
                                CartItemTypes.Earmold,
                                CartItemTypes.SoundGearEarmold,
                            ].includes(orderDetails.cartItemTypeId!) &&
                                isEarmold &&
                                ![12, 43].includes(option.id)) ||
                            ///product Options
                            (![7, 12, 14].includes(option.id) &&
                                countriesOldAccessories.includes(
                                    session!.country
                                ) &&
                                ((orderDetails.cartItemTypeId ===
                                    CartItemTypes.RicWithAp &&
                                    option.id === 4) ||
                                    ![4, 10, 3, 46].includes(option.id)) &&
                                (!option.english.includes('Receiver') ||
                                    orderDetails.orderType === 'patient')) ||
                            (orderDetails.cros &&
                                orderDetails.orderType === 'patient' &&
                                [8, 19].includes(option.id)) ||
                            (![7, 12, 13, 14].includes(option.id) &&
                                ((orderDetails.cartItemTypeId ===
                                    CartItemTypes.RicWithAp &&
                                    option.id === 4) ||
                                    ![4, 10, 3, 46].includes(option.id)) &&
                                (!option.english.includes('Receiver') ||
                                    orderDetails.orderType === 'patient'))
                        ) {
                            if (isOptionCategoryVisible(option)) {
                                return (
                                    <RowElementWrapper key={option.name + i}>
                                        <div className="col-sm-6">
                                            <OptionHeader
                                                option={option}
                                                productDetails={productDetails}
                                                patientOrder={patientOrder}
                                                orderDetails={orderDetails}
                                                selectedIdsState={
                                                    selectedIdsState
                                                }
                                                countryCode={session!.country}
                                            />
                                        </div>
                                        <div
                                            className="col-sm-6"
                                            style={{
                                                marginTop: '30px',
                                            }}
                                        >
                                            {option.items.map(
                                                (optionType, i) => {
                                                    const side =
                                                        orderDetails.side
                                                            ? orderDetails.side
                                                            : orderDetails.cros
                                                            ? 'L'
                                                            : 'B';
                                                    if (
                                                        optionType.kind ===
                                                        'hidden'
                                                    ) {
                                                        return <div key={i} />;
                                                    }
                                                    if (
                                                        isChargerMulti(
                                                            optionType.id
                                                        )
                                                    ) {
                                                        return (
                                                            <RowElementWrapper
                                                                key={
                                                                    optionType.name +
                                                                    i
                                                                }
                                                            >
                                                                <div className="col-sm-8">
                                                                    <MultiOption
                                                                        options={
                                                                            optionType.items
                                                                        }
                                                                        onChange={
                                                                            updateOptionsSelection
                                                                        }
                                                                        selectedIds={
                                                                            selectedIdsState
                                                                        }
                                                                        side={
                                                                            side ===
                                                                            'B'
                                                                                ? sides.binaural
                                                                                : side ===
                                                                                  'R'
                                                                                ? sides.right
                                                                                : sides.left
                                                                        }
                                                                        isAccessory={
                                                                            true
                                                                        }
                                                                        onAccessoryChange={(
                                                                            event
                                                                        ) => {
                                                                            const partNumber =
                                                                                event
                                                                                    .target
                                                                                    .value;
                                                                            const isPresent =
                                                                                selectedAccessories!.some(
                                                                                    (
                                                                                        accessory
                                                                                    ) =>
                                                                                        accessory.partNumber ===
                                                                                        partNumber
                                                                                );
                                                                            if (
                                                                                isPresent
                                                                            ) {
                                                                                onAddDeleteAccessory!(
                                                                                    partNumber,
                                                                                    0
                                                                                );
                                                                            } else {
                                                                                onAddDeleteAccessory!(
                                                                                    partNumber,
                                                                                    1,
                                                                                    true
                                                                                );
                                                                            }
                                                                        }}
                                                                        selectedAccessories={
                                                                            selectedAccessories
                                                                        }
                                                                    />
                                                                </div>
                                                            </RowElementWrapper>
                                                        );
                                                    }
                                                    if (
                                                        isChargerRadio(
                                                            optionType.id
                                                        )
                                                    ) {
                                                        return optionType.items
                                                            .length === 1 ? (
                                                            <GreenCheckMark
                                                                name={
                                                                    optionType
                                                                        .items[0]
                                                                        .name
                                                                }
                                                                key={i}
                                                            />
                                                        ) : (
                                                            <OptionsRadio
                                                                key={
                                                                    option.name +
                                                                    i
                                                                }
                                                                name={
                                                                    optionType.name
                                                                }
                                                                options={Array.from(
                                                                    new Set(
                                                                        optionType.items.filter(
                                                                            (
                                                                                x
                                                                            ) => {
                                                                                if (
                                                                                    (x.isAvailableLeft ||
                                                                                        x.isAvailableRight) &&
                                                                                    !x.hidden
                                                                                ) {
                                                                                    return {
                                                                                        value: x.id,
                                                                                        text: x.name,
                                                                                    };
                                                                                }
                                                                            }
                                                                        )
                                                                    )
                                                                )}
                                                                value={
                                                                    selectedAccessories!
                                                                        .length
                                                                        ? selectedAccessories!
                                                                              .map(
                                                                                  (
                                                                                      accessory
                                                                                  ) =>
                                                                                      accessory.partNumber
                                                                              )
                                                                              .find(
                                                                                  (
                                                                                      partNumber
                                                                                  ) =>
                                                                                      optionType.items
                                                                                          .map(
                                                                                              (
                                                                                                  item
                                                                                              ) =>
                                                                                                  item.partNumber
                                                                                          )
                                                                                          .includes(
                                                                                              partNumber
                                                                                          )
                                                                              )
                                                                        : ''
                                                                }
                                                                onChange={(
                                                                    event
                                                                ) => {
                                                                    const targetId =
                                                                        event
                                                                            .target
                                                                            .id;
                                                                    if (
                                                                        !targetId
                                                                    ) {
                                                                        onAddDeleteAccessory!(
                                                                            targetId,
                                                                            0
                                                                        );
                                                                    } else {
                                                                        const [
                                                                            chargerIdToDelete,
                                                                        ] =
                                                                            selectedAccessories!
                                                                                .filter(
                                                                                    (
                                                                                        accessory
                                                                                    ) =>
                                                                                        (accessory.isCharger ===
                                                                                            true ||
                                                                                            accessory.productType ===
                                                                                                BATTERY_PRODUCT_TYPE) &&
                                                                                        accessory.partNumber !==
                                                                                            targetId
                                                                                )
                                                                                .map(
                                                                                    (
                                                                                        accessory
                                                                                    ) =>
                                                                                        accessory.partNumber
                                                                                );
                                                                        onAddDeleteAccessory!(
                                                                            targetId,
                                                                            1,
                                                                            true,
                                                                            chargerIdToDelete
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    }
                                                    //you'll need to add your new country to the countryOldAccessory array to get this
                                                    if (
                                                        optionType.id === 102 ||
                                                        optionType.id ===
                                                            1005 ||
                                                        optionType.id ===
                                                            1006 ||
                                                        isChargerAccessory(
                                                            optionType.id
                                                        )
                                                    )
                                                        return (
                                                            <>
                                                                {optionType.items.map(
                                                                    (
                                                                        option
                                                                    ) => {
                                                                        if (
                                                                            option.hidden ||
                                                                            option.name ===
                                                                                NO_CHARGER
                                                                        )
                                                                            return;
                                                                        const selectedAccessory =
                                                                            selectedAccessories!.find(
                                                                                (
                                                                                    x
                                                                                ) =>
                                                                                    x.partNumber ===
                                                                                    option.partNumber
                                                                            );
                                                                        return (
                                                                            <AccessoryElementIntl
                                                                                key={
                                                                                    option.name
                                                                                }
                                                                                accesoryName={
                                                                                    option.name
                                                                                }
                                                                                productNumber={
                                                                                    option.partNumber
                                                                                }
                                                                                quantity={
                                                                                    selectedAccessory
                                                                                        ? selectedAccessory!
                                                                                              .quantity
                                                                                        : 0
                                                                                }
                                                                                onQuantityChanged={
                                                                                    onAddDeleteAccessory!
                                                                                }
                                                                                patientOrder={
                                                                                    patientOrder!
                                                                                }
                                                                                optionTypeId={
                                                                                    optionType.id
                                                                                }
                                                                            />
                                                                        );
                                                                    }
                                                                )}
                                                            </>
                                                        );
                                                    if (
                                                        optionType.kind ===
                                                            'select' ||
                                                        optionType.kind ===
                                                            'select-nd'
                                                    ) {
                                                        return (
                                                            optionType.items.some(
                                                                (x) =>
                                                                    (side ===
                                                                        'B' &&
                                                                        (x.isAvailableRight ||
                                                                            x.isAvailableLeft)) ||
                                                                    (side ===
                                                                        'L' &&
                                                                        x.isAvailableLeft) ||
                                                                    (side ===
                                                                        'R' &&
                                                                        x.isAvailableRight)
                                                            ) && (
                                                                <RowElementWrapper
                                                                    key={
                                                                        optionType.name
                                                                    }
                                                                >
                                                                    <div className="col-sm-8">
                                                                        {optionType
                                                                            .items
                                                                            .length ===
                                                                            1 ||
                                                                        optionType.items.filter(
                                                                            (
                                                                                x
                                                                            ) =>
                                                                                (side !==
                                                                                    'L' &&
                                                                                    x.isAvailableRight) ||
                                                                                (side !==
                                                                                    'R' &&
                                                                                    x.isAvailableLeft)
                                                                        )
                                                                            .length ===
                                                                            1 ? (
                                                                            <GreenCheckMark
                                                                                name={
                                                                                    optionType.items.find(
                                                                                        (
                                                                                            x
                                                                                        ) =>
                                                                                            (side !==
                                                                                                'L' &&
                                                                                                x.isAvailableRight) ||
                                                                                            (side !==
                                                                                                'R' &&
                                                                                                x.isAvailableLeft)
                                                                                    )!
                                                                                        .name
                                                                                }
                                                                                key={
                                                                                    i
                                                                                }
                                                                            />
                                                                        ) : (
                                                                            <OptionsSelectDropDown
                                                                                key={
                                                                                    optionType.name +
                                                                                    i
                                                                                }
                                                                                name={
                                                                                    optionType.name
                                                                                }
                                                                                value={
                                                                                    optionType.items.find(
                                                                                        (
                                                                                            x
                                                                                        ) =>
                                                                                            (side !==
                                                                                                'L' &&
                                                                                                selectedIdsState.rightSelectedIds.includes(
                                                                                                    x.id
                                                                                                )) ||
                                                                                            (side !==
                                                                                                'R' &&
                                                                                                selectedIdsState.leftSelectedIds.includes(
                                                                                                    x.id
                                                                                                ))
                                                                                    )
                                                                                        ? optionType.items.find(
                                                                                              (
                                                                                                  x
                                                                                              ) =>
                                                                                                  (side !==
                                                                                                      'L' &&
                                                                                                      selectedIdsState.rightSelectedIds.includes(
                                                                                                          x.id
                                                                                                      )) ||
                                                                                                  (side !==
                                                                                                      'R' &&
                                                                                                      selectedIdsState.leftSelectedIds.includes(
                                                                                                          x.id
                                                                                                      ))
                                                                                          )!
                                                                                              .id
                                                                                        : undefined
                                                                                }
                                                                                options={Array.from(
                                                                                    new Set(
                                                                                        optionType.items.filter(
                                                                                            (
                                                                                                x
                                                                                            ) => {
                                                                                                if (
                                                                                                    ((side !==
                                                                                                        'L' &&
                                                                                                        x.isAvailableRight) ||
                                                                                                        (side !==
                                                                                                            'R' &&
                                                                                                            x.isAvailableLeft)) &&
                                                                                                    !x.hidden
                                                                                                )
                                                                                                    return {
                                                                                                        value: x.id,
                                                                                                        text: x.name,
                                                                                                    };
                                                                                            }
                                                                                        )
                                                                                    )
                                                                                )}
                                                                                onChange={(
                                                                                    event
                                                                                ) =>
                                                                                    addSelectsToOrder(
                                                                                        optionType.items,
                                                                                        event,
                                                                                        'both'
                                                                                    )
                                                                                }
                                                                                fieldClassName={
                                                                                    'col-sm-12'
                                                                                }
                                                                            />
                                                                        )}
                                                                        {optionType.id ===
                                                                            32 &&
                                                                            optionType
                                                                                .items
                                                                                .length >
                                                                                1 &&
                                                                            optionType.items.filter(
                                                                                (
                                                                                    x
                                                                                ) =>
                                                                                    (side !==
                                                                                        'L' &&
                                                                                        x.isAvailableRight) ||
                                                                                    (side !==
                                                                                        'R' &&
                                                                                        x.isAvailableLeft)
                                                                            )
                                                                                .length >
                                                                                1 && (
                                                                                <span
                                                                                    className="delete-items"
                                                                                    onMouseEnter={(
                                                                                        e
                                                                                    ) => {
                                                                                        setColorSwatches(
                                                                                            true
                                                                                        );
                                                                                    }}
                                                                                    onMouseLeave={(
                                                                                        e
                                                                                    ) => {
                                                                                        setColorSwatches(
                                                                                            false
                                                                                        );
                                                                                    }}
                                                                                    style={{
                                                                                        marginLeft:
                                                                                            '16px',
                                                                                    }}
                                                                                >
                                                                                    {intl.formatMessage(
                                                                                        {
                                                                                            id: 'Color_Guide',
                                                                                            defaultMessage:
                                                                                                'Color Guide',
                                                                                        }
                                                                                    )}
                                                                                </span>
                                                                            )}
                                                                    </div>
                                                                    {colorSwatches &&
                                                                        optionType.id ===
                                                                            32 && (
                                                                            <div className="swatch-window">
                                                                                <img
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        height: 'auto',
                                                                                    }}
                                                                                    src="/estore/images/swatchWindow.jpg"
                                                                                    alt="color swatches for earmolds"
                                                                                />
                                                                            </div>
                                                                        )}
                                                                </RowElementWrapper>
                                                            )
                                                        );
                                                    }
                                                    if (
                                                        optionType.kind ===
                                                            'dual-select' ||
                                                        optionType.kind ===
                                                            'dual-select-nd'
                                                    ) {
                                                        return (
                                                            <RowElementWrapper
                                                                key={
                                                                    optionType.name
                                                                }
                                                            >
                                                                <div className="col-sm-8">
                                                                    <DualSelect
                                                                        optionType={
                                                                            optionType
                                                                        }
                                                                    />
                                                                </div>
                                                            </RowElementWrapper>
                                                        );
                                                    }
                                                    if (
                                                        optionType.kind ===
                                                        'multi'
                                                    ) {
                                                        return (
                                                            <RowElementWrapper
                                                                key={
                                                                    optionType.name
                                                                }
                                                            >
                                                                <div className="col-sm-8">
                                                                    <MultiOption
                                                                        options={
                                                                            optionType.items
                                                                        }
                                                                        onChange={
                                                                            updateOptionsSelection
                                                                        }
                                                                        selectedIds={
                                                                            selectedIdsState
                                                                        }
                                                                        side={
                                                                            side ===
                                                                            'B'
                                                                                ? sides.binaural
                                                                                : side ===
                                                                                  'R'
                                                                                ? sides.right
                                                                                : sides.left
                                                                        }
                                                                    />
                                                                </div>
                                                            </RowElementWrapper>
                                                        );
                                                    }
                                                    if (
                                                        optionType.kind ===
                                                        'dual-multi'
                                                    ) {
                                                        return (
                                                            <RowElementWrapper
                                                                key={
                                                                    optionType.name
                                                                }
                                                            >
                                                                <div className="col-sm-8">
                                                                    <SingleOptionType
                                                                        options={
                                                                            optionType.items
                                                                        }
                                                                        orderDetails={
                                                                            orderDetails
                                                                        }
                                                                        onChange={
                                                                            updateOptionsSelection
                                                                        }
                                                                        optionName={
                                                                            optionType.english
                                                                        }
                                                                        selectedSide={
                                                                            side ===
                                                                            'B'
                                                                                ? sides.binaural
                                                                                : side ===
                                                                                  'R'
                                                                                ? sides.right
                                                                                : sides.left
                                                                        }
                                                                        multiSelect={
                                                                            true
                                                                        }
                                                                        selectedIds={
                                                                            selectedIdsState
                                                                        }
                                                                    />
                                                                </div>
                                                            </RowElementWrapper>
                                                        );
                                                    }
                                                    if (
                                                        optionType.kind ===
                                                            'single' ||
                                                        optionType.kind ===
                                                            'single, inline'
                                                    ) {
                                                        return (
                                                            <RowElementWrapper
                                                                key={
                                                                    optionType.name
                                                                }
                                                            >
                                                                <div className="col-sm-8">
                                                                    <SingleInline
                                                                        orderDetails={
                                                                            orderDetails
                                                                        }
                                                                        optionType={
                                                                            optionType
                                                                        }
                                                                        selectedIdsState={
                                                                            selectedIdsState
                                                                        }
                                                                        side={
                                                                            side
                                                                        }
                                                                        addSelectsToOrder={
                                                                            addSelectsToOrder
                                                                        }
                                                                        updateCrosPairAid={
                                                                            updateCrosPairAid
                                                                        }
                                                                        productDetails={
                                                                            productDetails
                                                                        }
                                                                        optionObject={
                                                                            optionObject
                                                                        }
                                                                        isEarmold={
                                                                            isEarmold
                                                                        }
                                                                        country={
                                                                            session!
                                                                                .country
                                                                        }
                                                                    />
                                                                </div>
                                                            </RowElementWrapper>
                                                        );
                                                    }
                                                    if (
                                                        optionType.kind ===
                                                            'dual-single' ||
                                                        optionType.kind ===
                                                            'dual-single-nd'
                                                    ) {
                                                        return (
                                                            <RowElementWrapper
                                                                key={
                                                                    optionType.name
                                                                }
                                                            >
                                                                <div className="col-sm-12">
                                                                    <DualSingle
                                                                        orderDetails={
                                                                            orderDetails
                                                                        }
                                                                        optionType={
                                                                            optionType
                                                                        }
                                                                        optionObject={
                                                                            optionObject
                                                                        }
                                                                        side={
                                                                            side
                                                                        }
                                                                        productDetails={
                                                                            productDetails
                                                                        }
                                                                        selectedIdsState={
                                                                            selectedIdsState
                                                                        }
                                                                        updateCrosPairAid={
                                                                            updateCrosPairAid
                                                                        }
                                                                        updateOptionsSelection={
                                                                            updateOptionsSelection
                                                                        }
                                                                    />
                                                                </div>
                                                            </RowElementWrapper>
                                                        );
                                                    }
                                                    if (
                                                        optionType.kind ===
                                                        'display'
                                                    ) {
                                                        return (
                                                            <RowElementWrapper
                                                                key={
                                                                    optionType.name
                                                                }
                                                            >
                                                                <div className="col-sm-8">
                                                                    {optionType.items.map(
                                                                        (
                                                                            option
                                                                        ) => {
                                                                            if (
                                                                                side ===
                                                                                'R'
                                                                                    ? option.isAvailableRight &&
                                                                                      !option.hidden
                                                                                    : side ===
                                                                                      'L'
                                                                                    ? option.isAvailableLeft &&
                                                                                      !option.hidden
                                                                                    : (option.isAvailableLeft &&
                                                                                          !option.hidden) ||
                                                                                      (option.isAvailableRight &&
                                                                                          !option.hidden)
                                                                            ) {
                                                                                return (
                                                                                    <GreenCheckMark
                                                                                        key={
                                                                                            option.id
                                                                                        }
                                                                                        name={
                                                                                            option.name
                                                                                        }
                                                                                    />
                                                                                );
                                                                            } else {
                                                                                return (
                                                                                    <div
                                                                                        key={
                                                                                            i
                                                                                        }
                                                                                    />
                                                                                );
                                                                            }
                                                                        }
                                                                    )}
                                                                </div>
                                                            </RowElementWrapper>
                                                        );
                                                    }
                                                }
                                            )}
                                        </div>
                                    </RowElementWrapper>
                                );
                            }
                        }
                    })}
                </RowElementWrapper>
            </div>
        )
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        constantOptions: state.constants
            ? state.constants.productOptions
            : null,
        updateOptionData: state.updateOptionData
            ? state.updateOptionData
            : null,
        session: state.session ? state.session.data : null,
    };
};

const container = connect(mapStateToProps)(ProductOptions);

export default container;
